<template>
  <div class="throttle-card">
    <div class="card-header">
      <h4>Throttle Monitor</h4>
      <div class="refresh-indicator" v-if="loading">
        <div class="spinner-small"></div>
        <span>Refreshing...</span>
      </div>
    </div>

    <!-- Concurrent Connections -->
    <div class="throttle-stats">
      <div class="main-stat">
        <div class="stat-circle" :class="getStatusClass()">
          <div class="stat-circle-inner">
            <div class="stat-value">{{ throttleData.current_concurrent }}</div>
            <div class="stat-max">/ {{ throttleData.max_concurrent }}</div>
          </div>
          <svg class="progress-ring" :width="circleSize" :height="circleSize">
            <circle
              class="progress-ring-bg"
              :stroke-width="strokeWidth"
              fill="transparent"
              :r="normalizedRadius"
              :cx="circleSize / 2"
              :cy="circleSize / 2"
            />
            <circle
              class="progress-ring-progress"
              :class="getStatusClass()"
              :stroke-width="strokeWidth"
              :stroke-dasharray="circumference + ' ' + circumference"
              :style="{ strokeDashoffset: strokeDashoffset }"
              fill="transparent"
              :r="normalizedRadius"
              :cx="circleSize / 2"
              :cy="circleSize / 2"
            />
          </svg>
        </div>
        <div class="stat-label">Concurrent Connections</div>
      </div>

      <div class="side-stats">
        <div class="info-card">
          <div class="info-icon">
            <i class="bi bi-layers"></i>
          </div>
          <div class="info-content">
            <div class="info-label">Max Concurrent</div>
            <div class="info-value">{{ throttleData.max_concurrent }}</div>
          </div>
        </div>

        <div class="info-card" :class="getStatusClass()">
          <div class="info-icon">
            <i class="bi bi-speedometer2"></i>
          </div>
          <div class="info-content">
            <div class="info-label">Utilization</div>
            <div class="info-value">{{ utilizationPercent }}%</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Indicator -->
    <div class="status-bar" :class="getStatusClass()">
      <i :class="getStatusIcon()"></i>
      <span>{{ getStatusText() }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ThrottleMonitor',
  props: {
    throttleData: {
      type: Object,
      required: true,
      default: () => ({
        max_concurrent: 50,
        current_concurrent: 0
      })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      circleSize: 180,
      strokeWidth: 12
    };
  },
  computed: {
    utilizationPercent() {
      if (!this.throttleData.max_concurrent) return 0;
      return Math.round((this.throttleData.current_concurrent / this.throttleData.max_concurrent) * 100);
    },
    normalizedRadius() {
      return (this.circleSize - this.strokeWidth) / 2;
    },
    circumference() {
      return this.normalizedRadius * 2 * Math.PI;
    },
    strokeDashoffset() {
      const progress = this.utilizationPercent / 100;
      return this.circumference - progress * this.circumference;
    }
  },
  methods: {
    getStatusClass() {
      const percent = this.utilizationPercent;
      if (percent >= 90) return 'danger';
      if (percent >= 70) return 'warning';
      return 'normal';
    },
    getStatusIcon() {
      const status = this.getStatusClass();
      if (status === 'danger') return 'bi bi-exclamation-triangle-fill';
      if (status === 'warning') return 'bi bi-exclamation-circle-fill';
      return 'bi bi-check-circle-fill';
    },
    getStatusText() {
      const status = this.getStatusClass();
      if (status === 'danger') return 'Critical: High concurrent connections';
      if (status === 'warning') return 'Warning: Approaching limit';
      return 'Normal: System operating normally';
    }
  }
};
</script>

<style scoped>
.throttle-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.card-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.refresh-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #9ca3af;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.throttle-stats {
  display: flex;
  gap: 24px;
  align-items: center;
  margin-bottom: 24px;
}

.main-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.stat-circle {
  position: relative;
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-circle-inner {
  position: absolute;
  text-align: center;
}

.stat-value {
  font-size: 48px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.stat-max {
  font-size: 18px;
  color: #9ca3af;
  margin-top: 4px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.progress-ring {
  transform: rotate(-90deg);
}

.progress-ring-bg {
  stroke: var(--form-bg);
}

.progress-ring-progress {
  stroke: #10b981;
  transition: stroke-dashoffset 0.5s ease, stroke 0.3s ease;
}

.progress-ring-progress.warning {
  stroke: #f59e0b;
}

.progress-ring-progress.danger {
  stroke: #ef4444;
}

.side-stats {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 12px 16px;
  transition: border-color 0.2s ease;
}

.info-card:hover {
  border-color: var(--border-hover);
}

.info-card.warning {
  border-left: 3px solid #f59e0b;
}

.info-card.danger {
  border-left: 3px solid #ef4444;
}

.info-icon {
  font-size: 24px;
  color: var(--primary-start);
}

.info-card.warning .info-icon {
  color: #f59e0b;
}

.info-card.danger .info-icon {
  color: #ef4444;
}

.info-content {
  flex: 1;
}

.info-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  font-weight: 600;
  margin-bottom: 4px;
}

.info-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.status-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
}

.status-bar.normal {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid #10b981;
  color: #10b981;
}

.status-bar.warning {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid #f59e0b;
  color: #f59e0b;
}

.status-bar.danger {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
}

.status-bar i {
  font-size: 16px;
}
</style>
